import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ADMIN_ROLES    = ['tenant_admin', 'super_admin'];
const REVIEWER_ROLES = ['reviewer'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If set, user must have at least one of these roles. */
  requiredRoles?: string[];
  /** If true, admin/reviewer users are redirected to their own section. */
  companyOnly?: boolean;
}

export default function ProtectedRoute({ children, requiredRoles, companyOnly }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--background)' }}>
        <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Betöltés…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isAdmin    = user.roles.some((r) => ADMIN_ROLES.includes(r));
  const isReviewer = user.roles.some((r) => REVIEWER_ROLES.includes(r));

  // Role-gated routes: check against roles array
  if (requiredRoles && !user.roles.some((r) => requiredRoles.includes(r))) {
    if (isAdmin)    return <Navigate to="/admin/applications" replace />;
    if (isReviewer) return <Navigate to="/reviewer/applications" replace />;
    return <Navigate to="/" replace />;
  }

  // Company-only routes: redirect privileged users to their own section
  if (companyOnly) {
    if (isAdmin)    return <Navigate to="/admin/applications" replace />;
    if (isReviewer) return <Navigate to="/reviewer/applications" replace />;
  }

  return <>{children}</>;
}
